import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Select } from "../components/UI";
import { toast } from "sonner";
import { HandCoins, Users, Loader2, TrendingUp, TrendingDown, Info } from "lucide-react";

const usd = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const usdShort = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

const roasColor = (r) => {
  if (r == null) return "text-slate-500";
  if (r >= 3) return "text-emerald-300";
  if (r >= 1) return "text-cyan-300";
  return "text-rose-300";
};

const roasLabel = (r) => {
  if (r == null) return "N/A";
  if (r >= 3) return "Highly profitable";
  if (r >= 1) return "Profitable";
  if (r > 0) return "Losing money";
  return "N/A";
};

export default function SourceEconomics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/fundraising/source-economics?days=${days}`);
      setData(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load source economics"));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="source-economics-page">
      <PageHeader
        eyebrow="Fundraising Economics"
        title="Source Economics"
        subtitle="Cost per donor and return on ad spend (ROAS) by acquisition channel — see which ads are actually paying for themselves."
        action={
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="source-econ-window">
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last 12 months</option>
          </Select>
        }
      />

      {loading && <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>}

      {!loading && data && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="source-econ-totals">
            <Card>
              <div className="text-xs uppercase tracking-wider text-slate-400">Revenue</div>
              <div className="mt-2 font-display text-3xl font-bold text-white" data-testid="totals-revenue">{usdShort(data.totals.revenue)}</div>
              <div className="mt-1 text-xs text-slate-500">Total raised in window</div>
            </Card>
            <Card>
              <div className="text-xs uppercase tracking-wider text-slate-400">Attributed cost</div>
              <div className="mt-2 font-display text-3xl font-bold text-white" data-testid="totals-cost">{usdShort(data.totals.cost)}</div>
              <div className="mt-1 text-xs text-slate-500">Delivered media spend</div>
            </Card>
            <Card>
              <div className="text-xs uppercase tracking-wider text-slate-400">Unique donors</div>
              <div className="mt-2 font-display text-3xl font-bold text-white" data-testid="totals-donors">{data.totals.donors.toLocaleString()}</div>
              <div className="mt-1 text-xs text-slate-500">Deduped by email</div>
            </Card>
            <Card>
              <div className="text-xs uppercase tracking-wider text-slate-400">Blended ROAS</div>
              <div className={`mt-2 font-display text-3xl font-bold ${roasColor(data.totals.roas)}`} data-testid="totals-roas">
                {data.totals.roas == null ? "—" : `${data.totals.roas}×`}
              </div>
              <div className="mt-1 text-xs text-slate-500">Revenue ÷ cost</div>
            </Card>
          </div>

          <Card className="!p-0" data-testid="source-econ-table-card">
            <div className="px-6 py-4 border-b border-white/10 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-cyan-300" />
              <div className="font-display text-lg font-semibold text-white">By source</div>
              <div className="text-xs text-slate-500 ml-auto">Sorted by ROAS · window {data.window_days}d since {data.since}</div>
            </div>
            {data.rows.length === 0 && (
              <div className="p-12 text-center text-slate-500 text-sm">
                No contributions with attribution in this window. Set a utm_source on your fundraising landing links to populate this table.
              </div>
            )}
            {data.rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="source-econ-table">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                    <tr>
                      <th className="text-left px-6 py-3">Channel</th>
                      <th className="text-right px-4 py-3">Revenue</th>
                      <th className="text-right px-4 py-3">Cost</th>
                      <th className="text-right px-4 py-3">Donors</th>
                      <th className="text-right px-4 py-3">Cost / donor</th>
                      <th className="text-right px-6 py-3">ROAS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.source} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`source-row-${r.source}`}>
                        <td className="px-6 py-3.5">
                          <div className="font-medium text-white">{r.label}</div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{r.source}</div>
                        </td>
                        <td className="px-4 py-3.5 text-right font-mono text-cyan-200">{usd(r.revenue)}</td>
                        <td className="px-4 py-3.5 text-right font-mono text-slate-300">{usd(r.cost)}</td>
                        <td className="px-4 py-3.5 text-right"><span className="inline-flex items-center gap-1 text-slate-200"><Users className="w-3 h-3 text-cyan-400/60" /> {r.donor_count}</span></td>
                        <td className="px-4 py-3.5 text-right font-mono text-slate-300">{r.cost_per_donor == null ? "—" : usd(r.cost_per_donor)}</td>
                        <td className={`px-6 py-3.5 text-right ${roasColor(r.roas)}`}>
                          <div className="font-bold font-mono">{r.roas == null ? "—" : `${r.roas}×`}</div>
                          <div className="text-[9px] uppercase tracking-wider mt-0.5">{roasLabel(r.roas)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card>
            <div className="text-xs text-slate-400 flex items-start gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-cyan-400/70" />
              <div>{data.note}</div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
